import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { updateShipment, refreshExchangeRates } from 'services/shipments.service'
import { INCOTERMS, TRANSPORT_MODES, LAYERS, ALLOCATION_BASES } from 'config'
import { useI18n, apiError, formatRateSource } from 'i18n'

const inputClass = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none'
const COST_LAYERS = LAYERS.filter((l) => l.key !== 'merchandise')

const SettingsPanel = ({ shipment, onSaved }) => {
  const { t, fmt } = useI18n()
  const [form, setForm] = useState({
    reference: shipment.reference,
    incoterm: shipment.incoterm || '',
    transportMode: shipment.transportMode || '',
    mode: shipment.mode,
    reportCurrency: shipment.reportCurrency,
    vatRecoverable: shipment.vatRecoverable,
    exchangeRateDate: shipment.exchangeRateDate || '',
    rates: Object.entries(shipment.exchangeRates || {}).map(([currency, rate]) => ({ currency, rate: String(rate) })),
    overrides: shipment.allocationOverrides || {}
  })
  const [busy, setBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const meta = shipment.exchangeRateMeta || {}
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))
  const setRate = (i, key) => (e) => setForm((f) => {
    const rates = [...f.rates]
    rates[i] = { ...rates[i], [key]: e.target.value }
    return { ...f, rates }
  })
  const setOverride = (layer) => (e) => setForm((f) => {
    const overrides = { ...f.overrides }
    if (e.target.value) overrides[layer] = e.target.value
    else delete overrides[layer]
    return { ...f, overrides }
  })

  const onSave = async (event) => {
    event.preventDefault()
    setBusy(true)
    try {
      const exchangeRates = {}
      for (const r of form.rates) {
        if (r.currency && Number(r.rate) > 0) exchangeRates[r.currency.toUpperCase()] = Number(r.rate)
      }
      await updateShipment(shipment.shipmentId, {
        reference: form.reference,
        incoterm: form.incoterm || null,
        transportMode: form.transportMode || null,
        mode: form.mode,
        reportCurrency: form.reportCurrency.toUpperCase(),
        vatRecoverable: form.vatRecoverable,
        exchangeRateDate: form.exchangeRateDate || null,
        exchangeRates: Object.keys(exchangeRates).length ? exchangeRates : null,
        allocationOverrides: Object.keys(form.overrides).length ? form.overrides : null
      })
      toast.success(t('settings.saved'))
      onSaved()
    } catch (error) {
      toast.error(apiError(error, t('settings.saveFailed')))
    } finally {
      setBusy(false)
    }
  }

  // Saves pending edits first so the refresh uses the date on screen.
  const onRefresh = async () => {
    setRefreshing(true)
    try {
      await updateShipment(shipment.shipmentId, { exchangeRateDate: form.exchangeRateDate || null })
      const { updated, errors } = await refreshExchangeRates(shipment.shipmentId)
      if (updated.length) toast.success(t('settings.refreshed', { count: updated.length }))
      errors.forEach((e) => toast.error(t('settings.refreshFailed', { currency: e.currency, reason: t(`fx.errors.${e.code}`) })))
      if (!updated.length && !errors.length) toast(t('settings.refreshNothing'))
      onSaved()
    } catch (error) {
      toast.error(apiError(error, t('settings.saveFailed')))
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <form onSubmit={onSave} className='space-y-5 rounded-xl border border-slate-200 bg-white p-5'>
      <div className='grid gap-3 md:grid-cols-6'>
        <label className='flex flex-col gap-1 text-xs font-medium text-slate-500 md:col-span-2'>
          {t('settings.reference')}
          <input required value={form.reference} onChange={set('reference')} className={inputClass} />
        </label>
        <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
          {t('settings.incoterm')}
          <select value={form.incoterm} onChange={set('incoterm')} className={inputClass}>
            <option value=''>—</option>
            {INCOTERMS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
          {t('settings.transport')}
          <select value={form.transportMode} onChange={set('transportMode')} className={inputClass}>
            <option value=''>—</option>
            {TRANSPORT_MODES.map((m) => <option key={m} value={m}>{t(`transport.${m}`)}</option>)}
          </select>
        </label>
        <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
          {t('settings.mode')}
          <select value={form.mode} onChange={set('mode')} className={inputClass}>
            <option value='actual'>{t('modes.actualLong')}</option>
            <option value='estimate'>{t('modes.estimateLong')}</option>
          </select>
        </label>
        <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
          {t('settings.reportCurrency')}
          <input value={form.reportCurrency} onChange={set('reportCurrency')} maxLength={3} className={inputClass} />
        </label>
      </div>

      <label className='flex items-center gap-2 text-sm text-slate-700'>
        <input type='checkbox' checked={form.vatRecoverable} onChange={set('vatRecoverable')} />
        {t('settings.vatRecoverable')}
      </label>

      <div className='grid gap-6 md:grid-cols-2'>
        <div>
          <h3 className='mb-2 text-sm font-semibold text-slate-700'>{t('settings.ratesTitle', { currency: form.reportCurrency })}</h3>
          <p className='mb-2 text-xs text-slate-500'>{t('settings.ratesHelp')}</p>
          <label className='mb-3 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500'>
            {t('settings.rateDate')}
            <input type='date' value={form.exchangeRateDate} onChange={set('exchangeRateDate')} className={`${inputClass} w-40`} />
            <span className='font-normal text-slate-400'>{t('settings.rateDateHelp')}</span>
          </label>
          <div className='space-y-2'>
            {form.rates.map((r, i) => (
              <div key={i} className='flex flex-wrap items-center gap-2'>
                <input value={r.currency} onChange={setRate(i, 'currency')} placeholder='USD' maxLength={3} className={`${inputClass} w-20`} />
                <input type='number' step='0.0001' min='0' value={r.rate} onChange={setRate(i, 'rate')} placeholder='18.50' className={`${inputClass} w-32`} />
                <button type='button' onClick={() => setForm((f) => ({ ...f, rates: f.rates.filter((_, j) => j !== i) }))}
                  className='text-sm font-semibold text-status-incomplete hover:underline'>{t('common.remove')}</button>
                {meta[r.currency] && Number(shipment.exchangeRates?.[r.currency]) === Number(r.rate) && (
                  <span className='text-xs text-slate-400'>{formatRateSource(t, fmt.day, meta[r.currency])}</span>
                )}
              </div>
            ))}
            <div className='flex flex-wrap items-center gap-4'>
              <button type='button' onClick={() => setForm((f) => ({ ...f, rates: [...f.rates, { currency: '', rate: '' }] }))}
                className='text-sm font-semibold text-brand hover:underline'>{t('settings.addRate')}</button>
              <button type='button' onClick={onRefresh} disabled={refreshing}
                className='rounded-lg border border-slate-300 px-3 py-1 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50'>
                {refreshing ? t('settings.refreshing') : t('settings.refresh')}
              </button>
            </div>
          </div>
        </div>
        <div>
          <h3 className='mb-2 text-sm font-semibold text-slate-700'>{t('settings.allocationTitle')}</h3>
          <p className='mb-2 text-xs text-slate-500'>{t('settings.allocationHelp')}</p>
          <div className='grid gap-2 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2'>
            {COST_LAYERS.map((l) => (
              <label key={l.key} className='flex items-center justify-between gap-2 text-xs text-slate-600'>
                {t(`layers.${l.key}`)}
                <select value={form.overrides[l.key] || ''} onChange={setOverride(l.key)} className='min-w-0 rounded-lg border border-slate-300 px-2 py-1 text-xs'>
                  <option value=''>{t('common.default')}</option>
                  {ALLOCATION_BASES.map((b) => <option key={b} value={b}>{t(`bases.${b}`)}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className='flex justify-end'>
        <button type='submit' disabled={busy} className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
          {busy ? t('common.saving') : t('settings.save')}
        </button>
      </div>
    </form>
  )
}

export default SettingsPanel
